/** The scene contract, derived from the sdk document so the overlay cannot restate it. */

import type { SceneDocument, SnapshotFrame } from "@nebulize/sdk";

export type { SceneDocument };

/** One region as the transport carries it: its cells, as polygons. Derived, never restated. */
export type RegionMap = NonNullable<SnapshotFrame["regions"]>[number];

export type SceneNode = NonNullable<SceneDocument["nodes"]>[number];
export type Background = NonNullable<SceneDocument["background"]>;

/** The background as the wire carries it: absent, null, or one of the tagged layers. */
export type WireBackground = SceneDocument["background"];
export type SceneNodeKind = SceneNode["kind"];
export type LayoutBox = NonNullable<SceneNode["layout"]>;
export type DataBinding = NonNullable<SceneNode["bindings"]>[number];
export type CueBinding = NonNullable<SceneDocument["cue_bindings"]>[number];
export type Repeat = NonNullable<SceneNode["repeat"]>;
export type FlowLayout = NonNullable<SceneNode["flow"]>;

/** The one scene schema version this renderer draws. Any other document renders nothing. */
export const SUPPORTED_SCHEMA_VERSION = 1;

/** The node kinds this build draws. `SceneNodeKind` stays open, so this is a subset of it.
 *
 * `image` is absent: it needs the cloud asset resolver, exactly as the `image` background does.
 * `input` is absent, same as `image`.
 */
export const DRAWN_KINDS = [
  "group",
  "text",
  "bar",
  "background",
  "cells",
  "shape",
  "surface",
] as const;

/** The kind whose pixels the activity draws, not this renderer. OVL-016. */
export const SURFACE_KIND = "surface";

/** The key the background layer is drawn under, beneath every authored node in the document. */
export const BACKGROUND_KEY = "background";

export type DrawnKind = (typeof DRAWN_KINDS)[number];

const DRAWN: ReadonlySet<string> = new Set<string>(DRAWN_KINDS);

/** Whether this build draws the kind. An unlisted kind is not an error, it is not drawn. */
export function isDrawnKind(kind: SceneNodeKind): kind is DrawnKind {
  return DRAWN.has(kind);
}

/** The binding key a `bar` takes its magnitude from. */
export const BAR_VALUE_KEY = "value";

/** The binding keys that place a node from state instead of from its authored layout. */
export const LEFT_KEY = "left";
export const TOP_KEY = "top";

/** The binding key that shows one child of a node and hides its siblings. */
export const FRAME_KEY = "frame";
